import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Coffee, 
  UtensilsCrossed, 
  Wine,
  ShoppingCart,
  Landmark,
  TreePine,
  Train,
  ArrowLeft,
  ExternalLink,
  Star,
  Loader2,
  Mail,
  User,
  Check,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { trackEvent } from '../../utils/analytics';
import { useHubProperty, hubPath, PETWORTH_IDS } from './hubConfig';
import { HubLogo, HubEmailFooter } from './HubBrand';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Local Guide Page with Email Prompt
 * 
 * Curated local recommendations by category
 * Optional email capture to receive full list
 * 
 * Route: /hub/:propertyId/local-guide
 */

// Local recommendations data
const localRecommendations = {
  // Capitol Hill area
  'capitol-hill': {
    coffee: [
      { name: 'Peregrine Espresso', description: 'Local favorite, expertly crafted coffee', address: '660 Pennsylvania Ave SE', hostPick: true, maps: 'https://maps.google.com/?q=Peregrine+Espresso+Capitol+Hill+DC' },
      { name: 'Compass Coffee', description: 'Great cold brew and pastries', address: '1401 Pennsylvania Ave SE', hostPick: false, maps: 'https://maps.google.com/?q=Compass+Coffee+Capitol+Hill+DC' },
      { name: 'Swings Coffee', description: 'Historic DC roaster since 1916', address: 'Multiple locations', hostPick: false, maps: 'https://maps.google.com/?q=Swings+Coffee+DC' }
    ],
    restaurants: [
      { name: 'AMBAR', description: 'Unlimited Balkan small plates - incredible value', address: '523 8th St SE', hostPick: true, maps: 'https://maps.google.com/?q=AMBAR+Capitol+Hill+DC' },
      { name: 'Ted\'s Bulletin', description: 'Classic American diner, famous pop-tarts', address: '505 8th St SE', hostPick: true, maps: 'https://maps.google.com/?q=Teds+Bulletin+Capitol+Hill' },
      { name: 'CHIKO', description: 'Chinese-Korean fusion, James Beard nominated', address: '423 8th St SE', hostPick: false, maps: 'https://maps.google.com/?q=CHIKO+Capitol+Hill+DC' },
      { name: 'Belga Café', description: 'Belgian mussels & waffles', address: '514 8th St SE', hostPick: false, maps: 'https://maps.google.com/?q=Belga+Cafe+DC' },
      { name: 'Rose\'s Luxury', description: 'Michelin-starred, worth the wait', address: '717 8th St SE', hostPick: true, maps: 'https://maps.google.com/?q=Roses+Luxury+DC' }
    ],
    bars: [
      { name: 'Barrel', description: 'Whiskey bar with Southern food', address: '613 Pennsylvania Ave SE', hostPick: true, maps: 'https://maps.google.com/?q=Barrel+DC' },
      { name: 'Trusty\'s', description: 'Dive bar with great wings', address: '1420 Pennsylvania Ave SE', hostPick: false, maps: 'https://maps.google.com/?q=Trustys+DC' },
      { name: 'Tune Inn', description: 'Historic dive, Capitol Hill institution', address: '331 Pennsylvania Ave SE', hostPick: false, maps: 'https://maps.google.com/?q=Tune+Inn+DC' }
    ],
    groceries: [
      { name: 'Safeway', description: '24-hour grocery store', address: '415 14th St SE', hostPick: true, maps: 'https://maps.google.com/?q=Safeway+Capitol+Hill+DC' },
      { name: 'Harris Teeter', description: 'Full grocery, deli & prepared foods', address: '1350 Potomac Ave SE', hostPick: false, maps: 'https://maps.google.com/?q=Harris+Teeter+Potomac+Ave+DC' },
      { name: 'Eastern Market', description: 'Weekend farmers market & local vendors', address: '225 7th St SE', hostPick: true, maps: 'https://maps.google.com/?q=Eastern+Market+DC' }
    ],
    attractions: [
      { name: 'U.S. Capitol', description: 'Free tours available, book online', address: 'Capitol Hill', hostPick: true, maps: 'https://maps.google.com/?q=US+Capitol+Building' },
      { name: 'Library of Congress', description: 'World\'s largest library, stunning', address: '101 Independence Ave SE', hostPick: true, maps: 'https://maps.google.com/?q=Library+of+Congress+DC' },
      { name: 'Smithsonian Museums', description: 'All free! Air & Space, Natural History...', address: 'National Mall', hostPick: true, maps: 'https://maps.google.com/?q=Smithsonian+National+Mall+DC' },
      { name: 'National Mall', description: 'Lincoln Memorial to Capitol', address: 'Downtown', hostPick: false, maps: 'https://maps.google.com/?q=National+Mall+DC' }
    ],
    outdoors: [
      { name: 'Lincoln Park', description: 'Beautiful neighborhood park', address: 'E Capitol St NE', hostPick: true, maps: 'https://maps.google.com/?q=Lincoln+Park+DC' },
      { name: 'Capitol Riverfront', description: 'Waterfront trails and dining', address: 'Navy Yard', hostPick: false, maps: 'https://maps.google.com/?q=Capitol+Riverfront+DC' },
      { name: 'U.S. Botanic Garden', description: 'Free conservatory, beautiful gardens', address: '100 Maryland Ave SW', hostPick: true, maps: 'https://maps.google.com/?q=US+Botanic+Garden+DC' }
    ],
    transportation: [
      { name: 'Potomac Ave Metro', description: 'Blue/Orange/Silver lines', address: '5 min walk', hostPick: true, maps: 'https://maps.google.com/?q=Potomac+Ave+Metro+Station+DC' },
      { name: 'Eastern Market Metro', description: 'Blue/Orange/Silver lines', address: '10 min walk', hostPick: false, maps: 'https://maps.google.com/?q=Eastern+Market+Metro+Station+DC' },
      { name: 'Union Station', description: 'Amtrak, Red Line, shops', address: '15 min walk', hostPick: false, maps: 'https://maps.google.com/?q=Union+Station+DC' }
    ]
  },
  // Petworth/Taylor St area
  'petworth': {
    coffee: [
      { name: 'Petworth Citizen', description: 'Coffee shop + reading room', address: '829 Upshur St NW', hostPick: true, maps: 'https://maps.google.com/?q=Petworth+Citizen+DC' },
      { name: 'Qualia Coffee', description: 'Specialty roaster', address: '3917 Georgia Ave NW', hostPick: false, maps: 'https://maps.google.com/?q=Qualia+Coffee+DC' }
    ],
    restaurants: [
      { name: 'Timber Pizza', description: 'Wood-fired artisan pizza', address: '809 Upshur St NW', hostPick: true, maps: 'https://maps.google.com/?q=Timber+Pizza+DC' },
      { name: 'Purple Patch', description: 'Filipino comfort food', address: '3155 Mount Pleasant St NW', hostPick: true, maps: 'https://maps.google.com/?q=Purple+Patch+DC' },
      { name: 'Emissary', description: 'Modern American cuisine', address: '801 Upshur St NW', hostPick: false, maps: 'https://maps.google.com/?q=Emissary+DC' },
      { name: 'Slim\'s Diner', description: 'Classic breakfast spot', address: '5765 Georgia Ave NW', hostPick: false, maps: 'https://maps.google.com/?q=Slims+Diner+DC' }
    ],
    bars: [
      { name: 'Slash Run', description: 'Dive bar with burgers', address: '4534 Georgia Ave NW', hostPick: true, maps: 'https://maps.google.com/?q=Slash+Run+DC' },
      { name: 'Wonderland Ballroom', description: 'Neighborhood bar', address: '1101 Kenyon St NW', hostPick: false, maps: 'https://maps.google.com/?q=Wonderland+Ballroom+DC' }
    ],
    groceries: [
      { name: 'Yes! Organic', description: 'Organic & natural foods', address: '4100 Georgia Ave NW', hostPick: true, maps: 'https://maps.google.com/?q=Yes+Organic+Market+Georgia+Ave+DC' },
      { name: 'Safeway', description: 'Full grocery store', address: '3830 Georgia Ave NW', hostPick: false, maps: 'https://maps.google.com/?q=Safeway+Georgia+Ave+DC' }
    ],
    attractions: [
      { name: 'National Zoo', description: 'Free admission, pandas!', address: '3001 Connecticut Ave NW', hostPick: true, maps: 'https://maps.google.com/?q=National+Zoo+DC' },
      { name: 'Howard University', description: 'Historic HBCU campus', address: '2400 6th St NW', hostPick: false, maps: 'https://maps.google.com/?q=Howard+University+DC' },
      { name: 'U Street Corridor', description: 'Live music, nightlife', address: 'U Street', hostPick: true, maps: 'https://maps.google.com/?q=U+Street+Corridor+DC' }
    ],
    outdoors: [
      { name: 'Rock Creek Park', description: 'Miles of hiking trails', address: 'Nearby', hostPick: true, maps: 'https://maps.google.com/?q=Rock+Creek+Park+DC' },
      { name: 'Meridian Hill Park', description: 'Sunday drum circle', address: '16th St NW', hostPick: true, maps: 'https://maps.google.com/?q=Meridian+Hill+Park+DC' }
    ],
    transportation: [
      { name: 'Georgia Ave-Petworth Metro', description: 'Green/Yellow lines', address: '8 min walk', hostPick: true, maps: 'https://maps.google.com/?q=Georgia+Ave+Petworth+Metro+DC' },
      { name: 'Columbia Heights Metro', description: 'Green/Yellow lines', address: '15 min walk', hostPick: false, maps: 'https://maps.google.com/?q=Columbia+Heights+Metro+DC' }
    ]
  }
};

const categories = [
  { id: 'coffee', label: 'Coffee', icon: Coffee },
  { id: 'restaurants', label: 'Restaurants', icon: UtensilsCrossed },
  { id: 'bars', label: 'Bars', icon: Wine },
  { id: 'groceries', label: 'Groceries', icon: ShoppingCart },
  { id: 'attractions', label: 'Attractions', icon: Landmark },
  { id: 'outdoors', label: 'Outdoors', icon: TreePine },
  { id: 'transportation', label: 'Transportation', icon: Train }
];

const HubLocalGuide = () => {
  const { slug, propertyId } = useHubProperty();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState('coffee');
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Determine which area based on property
  const getArea = () => {
    if (!propertyId) return 'capitol-hill';
    // Capitol View properties (cv-004, cv-005, cv-006) are in Petworth
    if (PETWORTH_IDS.includes(propertyId)) {
      return 'petworth';
    }
    return 'capitol-hill';
  };

  const area = getArea();
  const recommendations = localRecommendations[area];

  useEffect(() => {
    const fetchProperty = async () => {
      try {
        trackEvent('local_guide_view', { property_id: propertyId, area });
        
        if (propertyId) {
          const response = await fetch(`${API_URL}/api/public/properties/${propertyId}`);
          if (response.ok) {
            const data = await response.json();
            setProperty(data);
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProperty();
  }, [propertyId, area]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/public/guest-hub/capture-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          first_name: firstName.trim(),
          source: 'local-guide',
          property_id: propertyId,
          property_slug: slug,
          opt_in: true
        })
      });
      trackEvent('local_guide_email_captured', { property_id: propertyId });
      setEmailSubmitted(true);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
    trackEvent('local_guide_category', { category: categoryId, property_id: propertyId });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div data-testid="hub-local-guide-page" className="min-h-screen bg-slate-900 max-w-lg mx-auto">
      {/* Header */}
      <div className="px-4 pt-6 pb-4">
        <Link to={hubPath(slug)} data-testid="hub-back-link" className="inline-flex items-center text-slate-400 hover:text-white mb-4">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Hub
        </Link>
        
        <div className="text-center">
          <div className="mb-6"><HubLogo /></div>
          <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <MapPin className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-1">Local Guide</h1>
          <p className="text-slate-400 text-sm">
            {area === 'petworth' ? 'Petworth & Brightwood Area' : 'Capitol Hill & Barracks Row'}
          </p>
        </div>
      </div>

      {/* Email Prompt Banner */}
      {!emailSubmitted && (
        <div className="px-4 pb-4">
          <Card className="bg-amber-500/10 border-amber-500/30">
            <CardContent className="p-4">
              {!showEmailPrompt ? (
                <div className="text-center">
                  <p className="text-amber-200 text-sm mb-2">
                    Want this list sent to your email?
                  </p>
                  <Button
                    data-testid="local-guide-email-me-btn"
                    onClick={() => setShowEmailPrompt(true)}
                    variant="outline"
                    size="sm"
                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/20"
                  >
                    Yes, email me
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleEmailSubmit} className="space-y-3">
                  <Input
                    type="text"
                    data-testid="local-guide-gate-first-name"
                    placeholder="First name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                  <Input
                    type="email"
                    data-testid="local-guide-gate-email"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="bg-slate-800 border-slate-600 text-white"
                  />
                  <Button
                    type="submit"
                    data-testid="local-guide-gate-submit"
                    disabled={submitting}
                    className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send to Email'}
                  </Button>
                  <HubEmailFooter />
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      )}
      {emailSubmitted && (
        <div className="px-4 pb-4">
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-3 text-center">
              <p data-testid="local-guide-email-success" className="text-green-400 text-sm flex items-center justify-center gap-2">
                <Check className="h-4 w-4" />
                Sent! Check your inbox.
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Categories */}
      <div className="px-4 pb-8">
        <div className="space-y-3">
          {categories.map(category => {
            const Icon = category.icon;
            const items = recommendations[category.id] || [];
            const isExpanded = expandedCategory === category.id;
            
            return (
              <div key={category.id}>
                <button
                  onClick={() => toggleCategory(category.id)}
                  data-testid={`local-guide-category-${category.id}`}
                  className="w-full"
                >
                  <Card className={`border-slate-700 transition-all ${isExpanded ? 'bg-slate-700/50' : 'bg-slate-800/50 hover:bg-slate-700/30'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Icon className="h-5 w-5 text-amber-400" />
                          <span className="text-white font-medium">{category.label}</span>
                          <span className="text-slate-500 text-sm">({items.length})</span>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </button>
                
                {isExpanded && items.length > 0 && (
                  <div className="mt-2 space-y-2 pl-2">
                    {items.map((item, index) => (
                      <Card key={index} className="bg-slate-800/30 border-slate-700/50">
                        <CardContent className="p-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="text-white font-medium text-sm">{item.name}</h4>
                                {item.hostPick && (
                                  <span className="bg-amber-500/20 text-amber-400 text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                                    <Star className="h-3 w-3 fill-current" />
                                    Host Pick
                                  </span>
                                )}
                              </div>
                              <p className="text-slate-400 text-xs mb-1">{item.description}</p>
                              <p className="text-slate-500 text-xs">{item.address}</p>
                            </div>
                            <a
                              href={item.maps}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => trackEvent('local_guide_maps_click', { place: item.name })}
                              className="ml-2 p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 pb-8 text-center">
        <p className="text-slate-500 text-xs">
          Last updated December 2025
        </p>
      </div>
    </div>
  );
};

export default HubLocalGuide;
